import React, { ComponentProps, useRef, useState } from 'react';
import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';
import { BlockBar, BlockBarButton } from '../../../_lib/components/BlockBar';
import { Icons } from '../../../components/atoms/SVG';
import IForm from '../../../types/IForm';
import { asFetchableView, useRefreshView } from '../../../_lib/ViewFetcher';
import { Heading } from '../../../_lib/components/Heading/Heading';
import { DraggableLatestForumPostListItem } from '../partials/LatestForumPostsListItem';
import { SortableContentList } from '../../../_lib/components/SortableContentList';
import { useArrayEffect } from '../../../_lib/components/SortableContentList/SortableContentList';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { LatestForumPostsDeleteForm } from '../partials/LatestForumPostsDeleteForm';
import { LatestForumPostsEditableElement, LatestForumPostsForm } from '../LatestForumPostsTypes';
import { LatestForumPostsAddForm } from '../partials/LatestForumPostsAddForm';
import { SonForm } from '../../../_lib/Form2';

type LatestForumPostsEditableListProps = {
  items: LatestForumPostsEditableElement[];
  create: LatestForumPostsForm;
  updateOrder?: LatestForumPostsForm;
};

export function LatestForumPostsEdit({ items, create, updateOrder }: LatestForumPostsEditableListProps) {
  const { t } = useSonTranslation('LatestForumPosts');
  const ref = useRef<HTMLButtonElement>(null);
  const { delayedRefreshView } = useRefreshView();

  const [activeForm, setActiveForm] = useState<{
    type: 'add' | 'delete';
    form: LatestForumPostsForm | SonForm<any>;
  } | null>(null);

  /**
   * After successfully changes, modal should be closed
   * */
  useArrayEffect(() => setActiveForm(null), items);

  const closeModal = () => setActiveForm(null);
  const openAddForm = () => setActiveForm({ type: 'add', form: create });
  const openDeleteForm = (deleteForm: SonForm<any>) => setActiveForm({ type: 'delete', form: deleteForm });

  const switchActiveForm = () => {
    switch (activeForm?.type) {
      case 'delete':
        return (
          <ModalDialog closeModal={closeModal} headline={t('removeForum')} focusOnReturn={ref} initialFocusIndex={1}>
            <LatestForumPostsDeleteForm form={activeForm.form as SonForm<any>} refresh={delayedRefreshView} />
          </ModalDialog>
        );
      case 'add':
        return (
          <ModalDialog closeModal={closeModal} headline={t('addForum')} initialFocusIndex={1}>
            <LatestForumPostsAddForm form={activeForm.form as LatestForumPostsForm} refresh={delayedRefreshView} />
          </ModalDialog>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <BlockBar
        heading={t('addForum')}
        hierarchy={3}
        modifier={items?.length > 0 ? ['border-bottom', 'border-top'] : 'border-top'}
      >
        <BlockBarButton ref={ref} onClick={openAddForm} text={t('addForum')} svgSpriteId={Icons.ADD_PLUS} />
        <Heading hierarchy={4} className="invisible">
          {t('existingForums')}:
        </Heading>
      </BlockBar>
      <SortableContentList items={items || []} form={updateOrder as IForm}>
        {(item: LatestForumPostsEditableElement) => (
          <DraggableLatestForumPostListItem key={item.id} {...item} openDeleteForm={openDeleteForm} />
        )}
      </SortableContentList>
      {switchActiveForm()}
    </>
  );
}

export type LatestForumPostsEditView = ComponentProps<typeof LatestForumPostsEditView>;
export const LatestForumPostsEditView = asFetchableView(LatestForumPostsEdit);
